import {
  Box, ButtonBase, Drawer, List, ListItemButton, Tooltip, Typography, useTheme,
} from '@mui/material';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import { Fragment, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useActiveModule } from './useActiveModule';
import { usePreferencesStore } from '@/store/preferences';
import { layout, radii } from '@/theme/tokens';
import type { ModuleManifest } from '@/modules/manifest';

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

/**
 * The active module's sidebar, and nothing else.
 *
 * Which module that is comes from the URL (`activeModule`), never from state:
 * a deep link, a refresh and the back button all restore the same rail. The
 * shell's own pages — the apps grid, the dashboard, the profile — belong to no
 * module and get no sidebar at all, so they run full width.
 *
 * The brand is in the top bar above it; the rail opens with the module's own
 * icon tile and name. Selected is the one look the product uses everywhere —
 * the neutral fill, bold ink and the icon in the primary — never a tinted pill
 * or a coloured stripe (owner's rule). Collapsed, the rail is 68px of icons
 * whose names are tooltips.
 */
export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const { t } = useTranslation();
  const collapsed = usePreferencesStore((s) => s.sidebarCollapsed);
  const { module, sections, hasSidebar } = useActiveModule();
  const width = collapsed ? layout.sidebarCollapsedWidth : layout.sidebarWidth;

  // No module, or a module with no rail of its own: full-width page.
  if (!hasSidebar || !module) return null;

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { width: layout.sidebarMobileWidth, maxWidth: '86vw', border: 0 },
        }}
      >
        <RailContent module={module} sections={sections} collapsed={false} onNavigate={onMobileClose} />
      </Drawer>
      <Box
        component="nav"
        aria-label={t(module.titleKey)}
        sx={{
          display: { xs: 'none', lg: 'flex' },
          position: 'sticky',
          top: layout.topbarHeight,
          alignSelf: 'flex-start',
          height: `calc(100dvh - ${layout.topbarHeight}px)`,
          width,
          flexShrink: 0,
          bgcolor: 'background.paper',
          // `borderInlineEnd: 1` is not expanded by sx the way `borderRight` is.
          borderInlineEndWidth: '1px',
          borderInlineEndStyle: 'solid',
          borderColor: 'divider',
        }}
      >
        <RailContent module={module} sections={sections} collapsed={collapsed} />
      </Box>
    </>
  );
}

function RailContent({
  module,
  sections,
  collapsed,
  onNavigate,
}: {
  module: ModuleManifest;
  sections: ReturnType<typeof useActiveModule>['sections'];
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { pathname } = useLocation();
  const toggle = usePreferencesStore((s) => s.toggleSidebar);
  const ModuleIcon = module.icon;
  const moduleTitle = t(module.titleKey);

  // `placement="right"` is a physical value: in Arabic the tooltip would open
  // over the content it belongs beside. The rail is on the inline start in both
  // directions, so the tooltip follows the direction.
  const railTooltip = theme.direction === 'rtl' ? 'left' : 'right';
  const tip = (title: string, node: ReactNode) =>
    collapsed ? (
      <Tooltip title={title} placement={railTooltip} enterDelay={0}>
        <Box component="span" sx={{ display: 'flex' }}>
          {node}
        </Box>
      </Tooltip>
    ) : (
      node
    );

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: collapsed ? 'center' : 'stretch',
        py: collapsed ? 1.75 : 2.5,
        px: collapsed ? 0 : 1.5,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {/* The module's own header: its icon tile and its name. */}
      {tip(
        moduleTitle,
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: collapsed ? 0 : 1,
            pb: collapsed ? 0.25 : 1,
            minWidth: 0,
          }}
        >
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: collapsed ? 36 : 34,
              height: collapsed ? 36 : 34,
              borderRadius: `${radii.md}px`,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'brand.accentTint',
              color: theme.palette.mode === 'light' ? 'brand.primary' : 'brand.primaryInk',
              '& svg': { fontSize: 18 },
            }}
          >
            <ModuleIcon />
          </Box>
          {!collapsed ? (
            <Typography noWrap sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {moduleTitle}
            </Typography>
          ) : null}
        </Box>,
      )}

      {sections.map((section, index) => (
        <Fragment key={section.key}>
          {collapsed ? (
            <Box aria-hidden sx={{ width: 24, height: '1px', bgcolor: 'divider', my: 0.75, mt: index === 0 ? 1 : 0.75 }} />
          ) : section.labelKey ? (
            <Typography
              component="div"
              sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.disabled', px: 1.5, pt: 1.5, pb: 0.75 }}
            >
              {t(section.labelKey)}
            </Typography>
          ) : (
            <Box sx={{ height: 12 }} />
          )}
          <List
            disablePadding
            aria-label={section.labelKey ? t(section.labelKey) : undefined}
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: collapsed ? 'center' : 'stretch' }}
          >
            {section.items.map((item) => {
              const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
              const label = t(item.labelKey);
              return (
                <Box component="li" key={item.key} sx={{ listStyle: 'none', display: 'flex', flexDirection: 'column' }}>
                  {tip(
                    label,
                    <ListItemButton
                      component={NavLink}
                      to={item.to}
                      onClick={onNavigate}
                      aria-label={collapsed ? label : undefined}
                      sx={{
                        flex: collapsed ? '0 0 auto' : 1,
                        width: collapsed ? 44 : 'auto',
                        height: collapsed ? 44 : 40,
                        minHeight: 0,
                        borderRadius: `${collapsed ? radii.md : radii.sm}px`,
                        px: collapsed ? 0 : 1.5,
                        gap: 1.25,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        fontSize: '0.875rem',
                        fontWeight: active ? 600 : 500,
                        color: active ? 'text.primary' : 'text.secondary',
                        bgcolor: active ? 'brand.selected' : 'transparent',
                        '& svg': {
                          fontSize: 18,
                          flexShrink: 0,
                          color: active ? 'primary.main' : 'currentColor',
                        },
                        '&:hover': {
                          bgcolor: active ? 'brand.selected' : 'brand.hover',
                          color: 'text.primary',
                          boxShadow: collapsed && !active ? (th) => `inset 0 0 0 1px ${th.palette.divider}` : 'none',
                        },
                      }}
                    >
                      {item.icon}
                      {!collapsed ? (
                        <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {label}
                        </Box>
                      ) : null}
                    </ListItemButton>,
                  )}
                </Box>
              );
            })}
          </List>
        </Fragment>
      ))}

      {/* The way to fold the rail, at its foot; only where it can fold. */}
      {onNavigate ? null : (
        <Box sx={{ mt: 'auto', pt: 2, display: 'flex', width: '100%', justifyContent: 'center' }}>
          {tip(
            t('common.expand'),
            <ButtonBase
              onClick={toggle}
              aria-expanded={!collapsed}
              aria-label={collapsed ? t('common.expand') : undefined}
              sx={{
                width: collapsed ? 44 : '100%',
                height: collapsed ? 40 : 38,
                px: collapsed ? 0 : 1.5,
                gap: 1.25,
                borderRadius: `${collapsed ? radii.md : radii.sm}px`,
                justifyContent: collapsed ? 'center' : 'flex-start',
                color: 'text.disabled',
                fontSize: '0.8125rem',
                fontFamily: 'inherit',
                '&:hover': { bgcolor: 'brand.hover', color: 'text.primary' },
                '&.Mui-focusVisible': { boxShadow: (th) => `0 0 0 3px ${th.palette.brand.focusRing}` },
              }}
            >
              {/* The panel glyph's bar sits on the rail's side in both directions. */}
              <ViewSidebarOutlinedIcon
                sx={{ fontSize: 18, transform: 'scaleX(calc(var(--dir-scale, 1) * -1))' }}
              />
              {!collapsed ? t('common.collapse') : null}
            </ButtonBase>,
          )}
        </Box>
      )}
    </Box>
  );
}
